<script setup lang="ts">
import { computedAsync } from '@vueuse/core';

import {
  useBrowserStore,
  usePageConnectorStore,
  useRouterStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import type { HotelFilter } from '@apps/main/new-desktop/modules/hotelFilters';
import { useFiltersStore } from '@apps/main/new-desktop/core/stores/filters/filtersStore';
import { ChangeHotelEventSource } from '@apps/main/new-desktop/core/models';
import type { ChangeHotelEventMetadata } from '@apps/main/new-desktop/core/models';
import type { AccommodationSelectorType } from '@apps/main/new-desktop/modules/accommodationSelector';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';
import { useEditTripStore } from '@apps/main/new-desktop/core/stores/editTrip/editTripStore';
import { SuggestionType } from '@apps/main/new-desktop/modules/suggestionTypeSelector';

import { isExperimentAsync } from '@libs/feature-toggle';
import { AvailabilityOrder } from '@libs/trip-planner-interfaces';

import type { ItineraryAccommodationSegment } from '../../../models';
import {
  isAlternativeRoomSuggestionAccommodationSegment,
  isAvailableAccommodationSegment,
  isUnavailableAccommodationSegment,
} from '../../../models';
import {
  useTripOverviewModalsStore,
  useTripOverviewPanelsStore,
  useTripOverviewPageStore,
} from '../../../stores';

import AvailableAccommodationSegment from './AvailableAccommodationSegment.vue';
import AlternativeRoomSuggestionAccommodationSegment from './AlternativeRoomSuggestionAccommodationSegment.vue';
import UnavailableAccommodationSegment from './UnavailableAccommodationSegment.vue';

// NOTE: Important:
// * this component is smart
// * keep child components of this one dumb
// * every action in this component must be guarded by print mode flag

interface ItineraryAccommodationSegmentProps {
  segment: ItineraryAccommodationSegment;
  printMode: boolean;
}

const props = defineProps<ItineraryAccommodationSegmentProps>();

const browserStore = useBrowserStore();
const pageConnectorStore = usePageConnectorStore();
const statsigEventsStore = useStatsigEventsStore();
const tripOverviewModalsStore = useTripOverviewModalsStore();
const tripOverviewPanelsStore = useTripOverviewPanelsStore();
const tripOverviewPageStore = useTripOverviewPageStore();
const filtersStore = useFiltersStore();
const recipeStore = useRecipeStore();
const editTripStore = useEditTripStore();
const routerStore = useRouterStore();

const shouldHideProductLink = computedAsync<boolean>(
  () => isExperimentAsync('remove_product_links'),
  false
);

function onShowDetails(): void {
  if (props.printMode) {
    return;
  }

  if (!props.segment.productCardData) {
    throw new Error(
      'Expected productCardData when showing product card segment!'
    );
  }

  if (props.segment.context.hotelCode) {
    routerStore.navigateToHotelDetailsInTripOverview(
      { ingredientIndex: props.segment.context.ingredientIndex },
      props.segment.context.hotelCode
    );
  }
}

function onShowRooms(isAlternativeRoom: boolean): void {
  if (props.printMode) {
    return;
  }

  if (!props.segment.productCardData) {
    throw new Error(
      'Expected productCardData when showing product card segment!'
    );
  }

  if (isAlternativeRoom) {
    statsigEventsStore.logTripOverviewSeeAlternativeRoomsWhenNoAvail();
  }

  if (props.segment.context.hotelCode) {
    if (!isAlternativeRoom) {
      statsigEventsStore.logTripOverviewEditRoomClicked();
    }
    routerStore.navigateToHotelDetailsInTripOverview(
      { ingredientIndex: props.segment.context.ingredientIndex },
      props.segment.context.hotelCode,
      true
    );
  }
}

function confirmAlternative(): void {
  if (isAlternativeRoomSuggestionAccommodationSegment(props.segment)) {
    tripOverviewPageStore.confirmAlternativeRoomSelection(props.segment);
  }
}

function onHotelSelectionNavigationTriggered(
  source: ChangeHotelEventSource
): void {
  if (props.printMode) {
    return;
  }
  const metaData: ChangeHotelEventMetadata = { source };
  statsigEventsStore.logTripOverviewHotelChangeClicked(metaData);
}

function onShowFilterPanel(source: 'from-plus' | 'from-more'): void {
  if (props.printMode) {
    return;
  }

  statsigEventsStore.logTripOverviewPreferencesModalOpened({ source });

  if (browserStore.isSmallerThan900px) {
    tripOverviewPanelsStore.openHotelFiltersPanel(
      props.segment.context.ingredientId
    );
  } else {
    tripOverviewModalsStore.openHotelFiltersModal(
      props.segment.context.ingredientId
    );
  }
}

function onRemoveFilter(type: HotelFilter, id?: string): void {
  if (props.printMode) {
    return;
  }
  statsigEventsStore.logTripOverviewFiltersRemoved();
  filtersStore.removeFilterFromTripOverview(
    type,
    props.segment.context.ingredientId,
    id
  );
}

//NOTE: mix of onUpdateSuggestionType() and onToggleAccommodation() -> will need refactor to mix both logics
function onToggleAccommodation(
  selectedOption: AccommodationSelectorType
): void {
  if (props.printMode) {
    return;
  }
  if (selectedOption === 'noAccommodation') {
    const isDisabled = false;
    statsigEventsStore.logTripOverviewHotelToggle(isDisabled);

    editTripStore.removeAccommodationFromHotelIngredient(
      props.segment.context.ingredientId
    );
  } else {
    const ingredient = recipeStore.getHotelIngredient(
      props.segment.context.ingredientId
    );
    const availabilityOrder =
      selectedOption === 'popularAccommodation'
        ? AvailabilityOrder.Popular
        : AvailabilityOrder.Cheapest;

    ingredient.availabilityOrder = availabilityOrder;
  }
}

function onSuggestionTypeSelected(suggestion: SuggestionType): void {
  const ingredient = recipeStore.getHotelIngredient(
    props.segment.context.ingredientId
  );
  const availabilityOrder =
    suggestion === SuggestionType.MostPopular
      ? AvailabilityOrder.Popular
      : AvailabilityOrder.Cheapest;

  ingredient.availabilityOrder = availabilityOrder;
}

async function openHotelSelection(): Promise<void> {
  await routerStore.navigateToHotelSelection({
    ingredientIndex: props.segment.context.ingredientIndex,
  });
}

function removeAccommodation(): void {
  statsigEventsStore.logExperimentIngredientRemoved('accommodation');
  const destination =
    isAvailableAccommodationSegment(props.segment) ||
    isAlternativeRoomSuggestionAccommodationSegment(props.segment)
      ? props.segment.productCardData.productCardContent.subheader
      : undefined;
  tripOverviewModalsStore.openConfirmRemoveAccommodationModal(
    props.segment.context.ingredientId,
    destination
  );
}
</script>

<template>
  <AvailableAccommodationSegment
    v-if="isAvailableAccommodationSegment(props.segment)"
    :segment="props.segment"
    :ingredient-index="props.segment.context.ingredientIndex"
    :print-mode="props.printMode"
    :should-hide-product-link="shouldHideProductLink"
    @add-filter="onShowFilterPanel('from-plus')"
    @remove-filter="onRemoveFilter($event.type, $event.id)"
    @show-details="onShowDetails"
    @show-more-filters="onShowFilterPanel('from-more')"
    @show-rooms="onShowRooms(false)"
    @toggle-accommodation="onToggleAccommodation($event)"
    @hotel-selection-navigation-triggered="
      onHotelSelectionNavigationTriggered(
        ChangeHotelEventSource.AvailableAccommodation
      )
    "
    @remove-accommodation="removeAccommodation"
    @open-hotel-selection="openHotelSelection"
    @suggestion-type-selected="onSuggestionTypeSelected"
  />

  <AlternativeRoomSuggestionAccommodationSegment
    v-if="isAlternativeRoomSuggestionAccommodationSegment(props.segment)"
    :segment="props.segment"
    @add-filter="onShowFilterPanel('from-plus')"
    @change-hotel="
      onHotelSelectionNavigationTriggered(
        ChangeHotelEventSource.AlternativeAccommodation
      )
    "
    @confirm-alternative="confirmAlternative"
    @remove-filter="onRemoveFilter($event.type, $event.id)"
    @show-details="onShowDetails"
    @show-more-filters="onShowFilterPanel('from-more')"
    @show-rooms="onShowRooms(true)"
    @hotel-selection-navigation-triggered="
      onHotelSelectionNavigationTriggered(
        ChangeHotelEventSource.AlternativeAccommodation
      )
    "
    @toggle-accommodation="onToggleAccommodation($event)"
    @remove-accommodation="removeAccommodation"
    @open-hotel-selection="openHotelSelection"
  />

  <UnavailableAccommodationSegment
    v-if="isUnavailableAccommodationSegment(props.segment)"
    :segment="props.segment"
    :error-message="
      tripOverviewPageStore.getUnavailabilityMessage(
        props.segment.segmentType,
        props.segment.reasons
      )
    "
    :should-hide-product-link="shouldHideProductLink"
    @toggle-accommodation="onToggleAccommodation($event)"
    @add-filter="onShowFilterPanel('from-plus')"
    @edit-filters="
      pageConnectorStore.openHotelSelectionFilters(
        props.segment.context.ingredientIndex
      )
    "
    @explore-other-products="
      onHotelSelectionNavigationTriggered(
        ChangeHotelEventSource.UnavailableAccommodation
      )
    "
    @remove-filter="onRemoveFilter($event.type, $event.id)"
    @show-more-filters="onShowFilterPanel('from-more')"
    @try-another-date="tripOverviewPageStore.scrollToCalendar()"
  />
</template>
